import { z } from 'zod';

/**
 * Create Bounded Context Request DTO
 * 
 * Validates client input for bounded context creation
 */
export const CreateBoundedContextRequestSchema = z.object({
    name: z.string()
        .min(2, 'Bounded context name must be at least 2 characters')
        .max(100, 'Bounded context name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Name must start with a letter and contain only letters, numbers, spaces, and hyphens'),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters'),
    purpose: z.string()
        .min(20, 'Purpose must be at least 20 characters')
        .max(500, 'Purpose must be less than 500 characters'),
    domainId: z.string().uuid('Invalid domain ID format'),
    team: z.object({
        primaryTeam: z.string().max(100).optional(),
        collaboratingTeams: z.array(z.string().max(100)).max(10).optional()
    }).optional(),
    integrations: z.object({
        upstream: z.array(z.string().uuid()).max(20).optional(),
        downstream: z.array(z.string().uuid()).max(20).optional(),
        sharedKernel: z.array(z.string().uuid()).max(10).optional()
    }).optional()
});

export type CreateBoundedContextRequest = z.infer<typeof CreateBoundedContextRequestSchema>;

/**
 * Update Bounded Context Request DTO
 */
export const UpdateBoundedContextRequestSchema = z.object({
    name: z.string()
        .min(2, 'Bounded context name must be at least 2 characters')
        .max(100, 'Bounded context name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Name must start with a letter and contain only letters, numbers, spaces, and hyphens')
        .optional(),
    description: z.string()
        .min(10, 'Description must be at least 10 characters')
        .max(1000, 'Description must be less than 1000 characters')
        .optional(),
    purpose: z.string()
        .min(20, 'Purpose must be at least 20 characters')
        .max(500, 'Purpose must be less than 500 characters')
        .optional(),
    status: z.enum(['planned', 'development', 'production', 'deprecated']).optional(),
    team: z.object({
        primaryTeam: z.string().max(100).optional(),
        collaboratingTeams: z.array(z.string().max(100)).max(10).optional()
    }).optional(),
    integrations: z.object({
        upstream: z.array(z.string().uuid()).max(20).optional(),
        downstream: z.array(z.string().uuid()).max(20).optional(),
        sharedKernel: z.array(z.string().uuid()).max(10).optional()
    }).optional()
});

export type UpdateBoundedContextRequest = z.infer<typeof UpdateBoundedContextRequestSchema>;

/**
 * Get Bounded Contexts Query Parameters DTO
 */
export const GetBoundedContextsQuerySchema = z.object({
    domainId: z.string().uuid().optional(),
    status: z.enum(['planned', 'development', 'production', 'deprecated', 'all']).default('production'),
    limit: z.coerce.number().min(1).max(100).default(20),
    offset: z.coerce.number().min(0).default(0),
    search: z.string().max(100).optional(),
    includeAggregates: z.coerce.boolean().default(false)
});

export type GetBoundedContextsQuery = z.infer<typeof GetBoundedContextsQuerySchema>;

/**
 * Bounded Context Parameters DTO
 */
export const BoundedContextParamsSchema = z.object({
    contextId: z.string().uuid('Invalid bounded context ID format')
});

export type BoundedContextParams = z.infer<typeof BoundedContextParamsSchema>;